<template>
  <el-drawer
    :title="dialogTitle"
    :visible.sync="innerVisible"
    direction="rtl"
    size="88%"
    append-to-body
    :wrapper-closable="true"
    custom-class="protocol-param-settings-drawer"
    @closed="onClosed"
  >
    <div class="drawer-body">
      <div class="drawer-scroll">
        <div v-if="protocol" class="protocol-info">
          <el-descriptions :column="3" size="small" border>
            <el-descriptions-item label="协议编号">{{ protocol.protocolCode || '—' }}</el-descriptions-item>
            <el-descriptions-item label="协议名称">{{ protocol.protocolName || '—' }}</el-descriptions-item>
            <el-descriptions-item label="通讯类型">{{ protocol.commType || 'TCP' }}</el-descriptions-item>
            <el-descriptions-item label="应用模式">{{ applyModeLabel(protocol.applyMode) }}</el-descriptions-item>
            <el-descriptions-item label="端口号">{{ protocol.port != null ? protocol.port : '—' }}</el-descriptions-item>
            <el-descriptions-item label="版本">{{ protocol.version || '—' }}</el-descriptions-item>
            <el-descriptions-item label="读取命令" :span="3">
              <span class="cmd-text">{{ protocol.readCommand || '—' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="读取间隔">
              {{ protocol.readInterval ? `${protocol.readInterval} 秒` : '—' }}
            </el-descriptions-item>
            <el-descriptions-item label="协议说明" :span="2">{{ protocol.description || '—' }}</el-descriptions-item>
          </el-descriptions>
        </div>

        <div class="table-title-row">
          <span class="table-title">协议参数 <em>共 {{ total }} 条记录</em></span>
          <div class="toolbar-right">
            <el-button type="primary" size="small" icon="el-icon-plus" :disabled="!protocolId" @click="openCreate">新增参数</el-button>
            <gas-table-toolbar
              :columns="tableColumnDefs"
              :visible-map.sync="tableColumnVisible"
              :selected-count="selectedIds.length"
              :show-batch-delete="tableCanBatchDelete"
              @batch-delete="batchRemove"
            />
          </div>
        </div>

        <el-table
          :data="list"
          v-loading="loading"
          border
          size="small"
          :height="tableHeight"
          class="param-table"
          header-cell-class-name="param-table-header"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="44" align="center" />
          <el-table-column label="序号" width="64" align="center">
            <template slot-scope="{ $index }">{{ rowIndex($index) }}</template>
          </el-table-column>
          <el-table-column v-if="colVisible('paramName')" prop="paramName" label="参数名称" min-width="100" align="center" />
          <el-table-column v-if="colVisible('unit')" prop="unit" label="单位" width="88" align="center">
            <template slot-scope="{ row }">{{ row.unit || '—' }}</template>
          </el-table-column>
          <el-table-column v-if="colVisible('dataType')" label="数据类型" width="110" align="center">
            <template slot-scope="{ row }">{{ dataTypeLabel(row.dataType) }}</template>
          </el-table-column>
          <el-table-column v-if="isMqtt && colVisible('jsonPath')" label="JSON路径" min-width="140" align="center" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.jsonPath || '—' }}</template>
          </el-table-column>
          <el-table-column v-if="isMqtt && colVisible('jsonName')" label="JSON名称" min-width="110" align="center" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.jsonName || '—' }}</template>
          </el-table-column>
          <el-table-column v-if="!isMqtt && colVisible('startByte')" label="起始字节" width="120" align="center">
            <template slot-scope="{ row }">{{ subsetFieldText(row, 'startByte') }}</template>
          </el-table-column>
          <el-table-column v-if="!isMqtt && colVisible('byteCount')" label="字节数量" width="120" align="center">
            <template slot-scope="{ row }">{{ subsetFieldText(row, 'byteCount') }}</template>
          </el-table-column>
          <el-table-column v-if="!isMqtt && colVisible('parseMethod')" label="解析方式" min-width="120" align="center">
            <template slot-scope="{ row }">{{ subsetFieldText(row, 'parseMethod') }}</template>
          </el-table-column>
          <el-table-column v-if="colVisible('calcFormula')" label="计算公式" min-width="140" align="center" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.calcFormula || '—' }}</template>
          </el-table-column>
          <el-table-column v-if="colVisible('maxValue')" label="最大值" width="88" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.maxValue) }}</template>
          </el-table-column>
          <el-table-column v-if="colVisible('minValue')" label="最小值" width="88" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.minValue) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="96" align="center" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" size="small" class="op-edit" icon="el-icon-edit" @click="openEdit(row)" />
              <el-button type="text" size="small" class="op-del" icon="el-icon-delete" @click="removeParam(row)" />
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="drawer-footer">
        <el-pagination
          class="pager"
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :current-page="query.pageNum"
          :page-size="query.pageSize"
          :page-sizes="[20, 50, 100]"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
        <el-button @click="innerVisible = false">关闭</el-button>
      </div>
    </div>

    <el-dialog
      :title="paramDialogTitle"
      :visible.sync="paramDialogVisible"
      width="760px"
      append-to-body
      @closed="paramForm = {}"
    >
      <el-form :model="paramForm" label-width="100px" size="small">
        <el-form-item label="参数名称" required>
          <el-select
            v-if="!isMqtt"
            v-model="paramForm.paramName"
            filterable
            allow-create
            default-first-option
            style="width:100%"
            placeholder="请选择标况总量、压力等统计字段，也可自行输入"
            @change="onParamNameChange"
          >
            <el-option
              v-for="item in paramNameOptions"
              :key="item.name"
              :label="item.unit ? `${item.name}（${item.unit}）` : item.name"
              :value="item.name"
            />
          </el-select>
          <el-input
            v-else
            v-model="paramForm.paramName"
            placeholder="自定义显示名称，如 压力、电池电量、采集时间"
          />
        </el-form-item>
        <el-form-item label="单位">
          <el-input v-model="paramForm.unit" placeholder="如 ug/m³" />
        </el-form-item>
        <el-form-item label="数据类型">
          <el-select v-model="paramForm.dataType" style="width:100%" @change="onDataTypeChange">
            <el-option label="数值型" value="numeric" />
            <el-option label="字符型" value="string" />
            <el-option label="Unix时间" value="unix" />
            <el-option v-if="!isMqtt" label="子集数据" value="subset" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="isMqtt" label="JSON路径">
          <el-input v-model="paramForm.jsonPath" placeholder="如 datas、datas/data，根节点留空" />
        </el-form-item>
        <el-form-item v-if="isMqtt" label="JSON名称" required>
          <el-input v-model="paramForm.jsonName" placeholder="如 ts、press、battery" />
        </el-form-item>
        <template v-if="!isMqtt && !isSubsetDataType">
          <el-form-item label="起始字节">
            <el-input v-model.number="paramForm.startByte" />
          </el-form-item>
          <el-form-item label="字节数量">
            <el-input v-model.number="paramForm.byteCount" />
          </el-form-item>
          <el-form-item label="解析方式">
            <el-select v-model="paramForm.parseMethod" style="width:100%">
              <el-option v-for="item in parseMethodOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
        </template>
        <el-form-item v-if="isSubsetDataType" label="分拆项">
          <div class="subset-list">
            <div v-for="(seg, index) in paramForm.subsetSegments" :key="index" class="subset-row">
              <span class="subset-index">x{{ index + 1 }}</span>
              <el-input v-model.number="seg.startByte" placeholder="起始字节" />
              <el-input v-model.number="seg.byteCount" placeholder="字节数量" />
              <el-select v-model="seg.parseMethod" placeholder="解析方式">
                <el-option v-for="item in parseMethodOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
              <el-button
                type="text"
                icon="el-icon-delete"
                class="subset-remove"
                :disabled="paramForm.subsetSegments.length <= 1"
                @click="removeSubsetSegment(index)"
              />
            </div>
            <el-button type="text" icon="el-icon-plus" @click="addSubsetSegment">添加分拆</el-button>
          </div>
          <div class="form-tip">每一行是一段独立数据。x1、x2 按从上到下的顺序对应这些分拆项。</div>
        </el-form-item>
        <el-form-item v-if="!isUnixDataType" label="计算公式">
          <el-input
            v-model="paramForm.calcFormula"
            type="textarea"
            :rows="2"
            :placeholder="formulaPlaceholder"
          />
          <div class="form-tip">{{ paramFormulaTip }}</div>
        </el-form-item>
        <el-form-item v-else label="时间转换">
          <div class="form-tip">Unix 时间由程序自动转换为日期时间（yyyy-MM-dd HH:mm:ss），支持 10 位秒 / 13 位毫秒，无需填写计算公式。</div>
        </el-form-item>
        <el-form-item v-if="!isUnixDataType" label="最大值">
          <el-input v-model="paramForm.maxValue" />
        </el-form-item>
        <el-form-item v-if="!isUnixDataType" label="最小值">
          <el-input v-model="paramForm.minValue" />
        </el-form-item>
        <el-form-item label="排序号">
          <el-input v-model.number="paramForm.sortNo" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="paramDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitParamForm">保存</el-button>
      </span>
    </el-dialog>
  </el-drawer>
</template>

<script>
import gasTableEnhance from '@/mixins/gasTableEnhance'
import { gasApi } from '@/api/gas'
import { paramNameSelectOptions, standardParamPreset, unitForParamName } from '@/config/protocolParamNames'

const isMqttProtocol = (protocol) => {
  if (!protocol) return false
  const comm = String(protocol.commType || '').toLowerCase()
  const mode = String(protocol.applyMode || '').toLowerCase()
  return comm === 'mqtt' || mode === 'mqtt'
}

const PARSE_METHOD_OPTIONS = [
  { label: 'BCD码', value: 'BCD' },
  { label: 'HEX', value: 'HEX' },
  { label: 'ASCII', value: 'ASCII' },
  { label: 'UInt8', value: 'UINT8' },
  { label: 'Float大端', value: 'FLOAT_BE' },
  { label: 'Float小端', value: 'FLOAT_LE' },
  { label: 'Double大端', value: 'DOUBLE_BE' },
  { label: 'Double小端', value: 'DOUBLE_LE' },
  { label: 'UInt64大端', value: 'UINT64_BE' },
  { label: 'UInt64小端', value: 'UINT64_LE' },
  { label: 'UInt32大端', value: 'UINT32_BE' },
  { label: 'UInt32小端', value: 'UINT32_LE' },
  { label: 'Int32大端', value: 'INT32_BE' },
  { label: 'Int32小端', value: 'INT32_LE' },
  { label: 'UInt16大端', value: 'UINT16_BE' },
  { label: 'UInt16小端', value: 'UINT16_LE' },
  { label: 'Int16大端', value: 'INT16_BE' },
  { label: 'Int16小端', value: 'INT16_LE' }
]

const emptySubsetSegment = (seed) => ({
  startByte: seed && seed.startByte != null ? seed.startByte : null,
  byteCount: seed && seed.byteCount != null ? seed.byteCount : 4,
  parseMethod: (seed && seed.parseMethod) || 'FLOAT_BE'
})

const parseSubsetSegments = (row) => {
  if (!row) return []
  let parsed = row.subsetSegments
  if (typeof parsed === 'string' && parsed.trim()) {
    try {
      parsed = JSON.parse(parsed)
    } catch (e) {
      parsed = []
    }
  }
  if (!Array.isArray(parsed)) return []
  return parsed.map(item => emptySubsetSegment(item)).filter(item => item.byteCount)
}

const emptyParam = (protocol) => {
  const mqtt = isMqttProtocol(protocol)
  const type = (protocol && protocol.protocolType) || ''
  const hex = !mqtt && (type.includes('市电') || type.includes('低功耗') || type.includes('压力'))
  return {
    dataType: 'numeric',
    paramCategory: mqtt || !hex ? 'JSON' : 'HEX',
    jsonPath: '',
    jsonName: '',
    startByte: null,
    byteCount: hex ? 4 : 0,
    parseMethod: mqtt ? '' : (hex ? 'FLOAT_BE' : 'BCD'),
    calcFormula: '',
    subsetSegments: [],
    maxValue: null,
    minValue: null,
    sortNo: 0
  }
}

export default {
  name: 'ProtocolParamSettingsDialog',
  mixins: [gasTableEnhance],
  props: {
    visible: { type: Boolean, default: false },
    protocolId: { type: [Number, String], default: null }
  },
  data() {
    return {
      idField: 'paramId',
      removeApi: gasApi.protocol.paramRemove,
      batchRemoveApi: gasApi.protocol.paramBatchRemove,
      protocol: null,
      loading: false,
      list: [],
      total: 0,
      paramDialogVisible: false,
      paramDialogTitle: '新增参数',
      paramForm: {},
      parseMethodOptions: PARSE_METHOD_OPTIONS,
      query: {
        pageNum: 1,
        pageSize: 50,
        paramCategory: '',
        keyword: ''
      }
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    dialogTitle() {
      if (this.protocol && this.protocol.protocolName) {
        return `参数配置 — ${this.protocol.protocolName}`
      }
      return '参数配置'
    },
    isMqtt() {
      return isMqttProtocol(this.protocol)
    },
    paramNameOptions() {
      return paramNameSelectOptions(this.paramForm && this.paramForm.paramName)
    },
    isUnixDataType() {
      return String((this.paramForm && this.paramForm.dataType) || '').toLowerCase() === 'unix'
    },
    isSubsetDataType() {
      return String((this.paramForm && this.paramForm.dataType) || '').toLowerCase() === 'subset'
    },
    formulaPlaceholder() {
      if (this.isSubsetDataType) return '算术如 x1+x2、(x1*0.1)+x2；拼接如 x1 & "-" & x2。留空则用逗号拼接'
      return '用 x 表示解析原始值，如 x*0.1、(x+10)/2。留空则直接使用原始值'
    },
    tableHeight() {
      // 全屏抽屉：协议信息区 + 标题栏 + 底栏预留后，表格占满剩余高度
      const h = typeof window !== 'undefined' ? window.innerHeight : 900
      return Math.max(280, h - 360)
    },
    paramFormulaTip() {
      if (this.isSubsetDataType) {
        return 'x1、x2 按分拆顺序对应每一段。算术用 x1+x2、(x1*0.1)+x2；字符串拼接用 x1 & "-" & x2。留空则按逗号把各段拼在一起。'
      }
      if (this.isMqtt) {
        return 'MQTT 按 JSON 路径+名称取值。路径可填父节点（datas、datas/data）或含字段的完整路径（datas/ts）；名称填字段名（ts、press）。路径末尾已是名称时不会重复拼接。根级字段路径留空。报文里若有数组（如 datas），每个元素作为一条记录入库，根级字段会复制到每条记录。'
      }
      const type = (this.protocol && this.protocol.protocolType) || ''
      if (type === '市电压力远传' || type === '市电远传压力') {
        return '市电压力远传：前 4 路流量计按市电远传表计块解析（第 4 路寄存器 40069~40086，40087~40090 预留），压力模拟量从寄存器 40091 起。'
      }
      if (type.includes('压力')) {
        return '压力远传：起始字节可填 Modbus 数据偏移或寄存器号如 40091。建议参数名：泄漏浓度、进口压力、出口压力1/2/3、差压1/2。'
      }
      if (type.includes('低功耗')) {
        return '低功耗远传：起始字节是相对单条历史记录的偏移（信号强度=6、电池电量=7、标况总量=8…）。温度默认公式 x/100。'
      }
      if (type.includes('透传')) {
        return '市电透传远传：起始字节相对整包原始报文，不做 7B DDP 解封装，也不按表计块自动重复。默认按 Modbus 应答布局（标况累积=3）。'
      }
      if (type.includes('市电')) {
        return '市电远传：起始字节是 Modbus 数据偏移（标况累积=3、工况累积=7、温度=11…）。1V2/1V4 会按表计块自动重复这些参数。'
      }
      return '用 x 表示解析原始值，如 x*0.1、(x+10)/2。留空则直接使用原始值。'
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.$nextTick(() => this.openForProtocol())
      }
    },
    protocolId() {
      if (this.visible) this.openForProtocol()
    }
  },
  mounted() {
    this._onResize = () => {
      this.$forceUpdate()
    }
    window.addEventListener('resize', this._onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize)
  },
  created() {
    this.initTableColumns([
      { key: 'paramName', label: '参数名称' },
      { key: 'unit', label: '单位' },
      { key: 'dataType', label: '数据类型' },
      { key: 'jsonPath', label: 'JSON路径' },
      { key: 'jsonName', label: 'JSON名称' },
      { key: 'startByte', label: '起始字节' },
      { key: 'byteCount', label: '字节数量' },
      { key: 'parseMethod', label: '解析方式' },
      { key: 'calcFormula', label: '计算公式' },
      { key: 'maxValue', label: '最大值' },
      { key: 'minValue', label: '最小值' }
    ], 'protocol-param-settings-dialog')
  },
  methods: {
    emptyParam,
    applyModeLabel(v) {
      const map = {
        active_report: '主动上报模式',
        poll_dtu: '召唤采集模式',
        mqtt: 'MQTT协议'
      }
      return map[v] || v || '—'
    },
    async openForProtocol() {
      this.query.pageNum = 1
      this.query.paramCategory = ''
      this.query.keyword = ''
      this.selectedIds = []
      await this.loadProtocol()
      await this.loadData()
    },
    onClosed() {
      this.protocol = null
      this.list = []
      this.total = 0
      this.paramDialogVisible = false
      this.paramForm = {}
      this.selectedIds = []
    },
    async loadProtocol() {
      if (!this.protocolId) {
        this.protocol = null
        return
      }
      try {
        const res = await gasApi.protocol.detail(this.protocolId)
        this.protocol = res.data || null
      } catch (e) {
        this.protocol = null
      }
    },
    buildQuery() {
      const p = { ...this.query, protocolId: this.protocolId }
      Object.keys(p).forEach(k => {
        if (p[k] === '') p[k] = undefined
      })
      return p
    },
    async loadData() {
      if (!this.protocolId) return
      this.loading = true
      try {
        const res = await gasApi.protocol.paramList(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    handleSizeChange(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.query.pageNum = page
      this.loadData()
    },
    rowIndex(index) {
      const row = this.list[index]
      if (row && row.sortNo != null) return row.sortNo
      return (this.query.pageNum - 1) * this.query.pageSize + index
    },
    dataTypeLabel(v) {
      if (v === 'string') return '字符型'
      if (v === 'unix') return 'Unix时间'
      if (v === 'subset') return '子集数据'
      return '数值型'
    },
    parseMethodLabel(v) {
      const hit = PARSE_METHOD_OPTIONS.find(item => item.value === v)
      return (hit && hit.label) || v || 'BCD码'
    },
    subsetSegmentsOf(row) {
      return parseSubsetSegments(row)
    },
    subsetFieldText(row, field) {
      const segments = String((row && row.dataType) || '') === 'subset' ? parseSubsetSegments(row) : []
      if (!segments.length) {
        if (field === 'parseMethod') return this.parseMethodLabel(row && row.parseMethod)
        const value = row && row[field]
        return value != null && value !== '' ? value : 0
      }
      return segments.map(seg => (field === 'parseMethod' ? this.parseMethodLabel(seg.parseMethod) : seg[field])).join('、')
    },
    onDataTypeChange(value) {
      if (value !== 'subset') return
      if (!Array.isArray(this.paramForm.subsetSegments) || !this.paramForm.subsetSegments.length) {
        this.$set(this.paramForm, 'subsetSegments', [emptySubsetSegment(this.paramForm)])
      }
    },
    addSubsetSegment() {
      const list = Array.isArray(this.paramForm.subsetSegments) ? this.paramForm.subsetSegments.slice() : []
      const last = list[list.length - 1]
      const start = last && Number(last.startByte)
      const count = last && Number(last.byteCount)
      list.push(emptySubsetSegment({
        startByte: Number.isFinite(start) && Number.isFinite(count) ? start + count : null,
        byteCount: Number.isFinite(count) ? count : 4,
        parseMethod: (last && last.parseMethod) || 'FLOAT_BE'
      }))
      this.$set(this.paramForm, 'subsetSegments', list)
    },
    removeSubsetSegment(index) {
      const list = (this.paramForm.subsetSegments || []).slice()
      if (list.length <= 1) return
      list.splice(index, 1)
      this.$set(this.paramForm, 'subsetSegments', list)
    },
    formatNum(v) {
      if (v === null || v === undefined || v === '') return '—'
      return String(v)
    },
    onParamNameChange(name) {
      const preset = standardParamPreset(this.protocol, name)
      if (preset) {
        Object.keys(preset).forEach(key => this.$set(this.paramForm, key, preset[key]))
        return
      }
      const unit = unitForParamName(name)
      if (unit) this.$set(this.paramForm, 'unit', unit)
    },
    openCreate() {
      if (!this.protocolId) return this.$message.warning('协议无效')
      this.paramDialogTitle = '新增参数'
      this.paramForm = { ...emptyParam(this.protocol), protocolId: this.protocolId }
      this.paramDialogVisible = true
    },
    openEdit(row) {
      this.paramDialogTitle = '修改参数'
      const subsetSegments = parseSubsetSegments(row)
      this.paramForm = {
        ...row,
        subsetSegments: subsetSegments.length ? subsetSegments : [emptySubsetSegment(row)]
      }
      this.paramDialogVisible = true
    },
    async submitParamForm() {
      if (!this.paramForm.paramName) return this.$message.warning('请填写参数名称')
      if (this.isMqtt && !(this.paramForm.jsonName || '').trim()) {
        return this.$message.warning('请填写 JSON 名称')
      }
      const payload = { ...this.paramForm, protocolId: this.protocolId }
      if (this.isMqtt) {
        payload.paramCategory = 'JSON'
        payload.parseMethod = ''
        payload.startByte = 0
        payload.byteCount = 0
        payload.jsonPath = (payload.jsonPath || '').trim()
        payload.jsonName = (payload.jsonName || '').trim()
      }
      if (payload.dataType === 'subset') {
        const segments = (payload.subsetSegments || []).map(seg => ({
          startByte: Number(seg.startByte),
          byteCount: Number(seg.byteCount),
          parseMethod: seg.parseMethod || 'FLOAT_BE'
        }))
        if (!segments.length || segments.some(seg => !Number.isFinite(seg.startByte) || !Number.isFinite(seg.byteCount) || seg.byteCount <= 0)) {
          return this.$message.warning('请把每个分拆项的起始字节、字节数量和解析方式填完整')
        }
        payload.subsetSegments = JSON.stringify(segments)
        payload.startByte = segments[0].startByte
        payload.byteCount = segments[0].byteCount
        payload.parseMethod = segments[0].parseMethod
      } else {
        payload.subsetSegments = ''
      }
      if (payload.minValue === '' || payload.minValue === undefined) payload.minValue = null
      if (payload.maxValue === '' || payload.maxValue === undefined) payload.maxValue = null
      await gasApi.protocol.paramSave(payload)
      this.$message.success('保存成功')
      this.paramDialogVisible = false
      this.loadData()
    },
    async removeParam(row) {
      await this.$confirm('确认删除该参数？', '提示', { type: 'warning' })
      await gasApi.protocol.paramRemove(row.paramId)
      this.$message.success('删除成功')
      this.loadData()
    }
  }
}
</script>

<style scoped>
.drawer-body {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
}
.drawer-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 20px 12px;
  box-sizing: border-box;
}
.drawer-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  border-top: 1px solid #e5e6eb;
  background: #fff;
}
.protocol-info {
  margin-bottom: 16px;
}
.cmd-text {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  word-break: break-all;
}
.table-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
}
.table-title {
  font-size: 14px;
  font-weight: 600;
  color: #1d2129;
}
.table-title em {
  font-style: normal;
  font-weight: 400;
  color: #86909c;
  margin-left: 8px;
  font-size: 13px;
}
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.param-table >>> .param-table-header {
  background: #4e5969 !important;
  color: #fff;
  font-weight: 500;
}
.param-table >>> th.param-table-header .cell {
  color: #fff;
}
.op-edit {
  color: #00b42a;
  font-size: 16px;
}
.op-del {
  color: #f53f3f;
  font-size: 16px;
}
.pager {
  margin: 0;
  text-align: left;
}
.form-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #86909c;
  line-height: 1.5;
}
.subset-list {
  width: 100%;
}
.subset-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.subset-row .el-input {
  width: 110px;
}
.subset-row .el-select {
  flex: 1;
}
.subset-index {
  width: 28px;
  color: #4e5969;
  font-size: 12px;
}
.subset-remove {
  color: #f53f3f;
  font-size: 16px;
}
</style>

<style>
/* drawer 挂到 body，需非 scoped 才能作用到 custom-class */
.protocol-param-settings-drawer {
  height: 100% !important;
}
.protocol-param-settings-drawer .el-drawer__header {
  margin-bottom: 12px;
  padding: 16px 20px 0;
}
.protocol-param-settings-drawer .el-drawer__body {
  height: calc(100% - 55px);
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
}
</style>
